import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import { Tool } from '../tools/Tool';
import type { EnhancedToolResult, ToolDefinition, ToolExecutionContext } from '../types';

import { intToHex } from 'js/src/libs/color-utils';

import { contrastRatio, parseColor } from './lint/contrast';
import {
    parseDesignTokens,
    plotBoxOf,
    REQUIRED_DESIGN_TOKENS,
    tokenPx,
    TYPE_TOKENS,
} from './lint/designSystemTokens';

import { setHtmlDesignSystem } from 'js/src/data/html-design';

/** Matches the safe margin the bounds lint enforces, so a system can't licence a cramped edge. */
const MIN_MARGIN_PX = 80;
/** The readable-text norm the size lint enforces: the smallest type in the scale must clear it. */
const MIN_TYPE_PCT = 4.5;
/** The baseline is the plot box's own foot; a px of rounding between them is not a disagreement. */
const BASELINE_TOL_PX = 1;

/** The smallest type this canvas allows, which is what `rejections` measures --ds-type-label by. */
function typeFloorPx(canvasHeight: number): number {
    return Math.ceil((MIN_TYPE_PCT / 100) * canvasHeight);
}

export const writeDesignSystemSchema = z.object({
    look: z
        .string()
        .describe(
            'The look in one or two sentences, e.g. "flat and geometric, one accent colour, ' +
                'generous margins, no gradients". What the tokens below are expressing.',
        ),
    css: z
        .string()
        .describe(
            'CSS (no <style> tag) defining every --ds-* token on :root: ' +
                `${REQUIRED_DESIGN_TOKENS.join(', ')}. The three type sizes are px, largest ` +
                'first; --ds-margin and the --ds-plot-* box are px; --ds-baseline-y is ' +
                '--ds-plot-y + --ds-plot-h; --ds-accent, --ds-ink and --ds-bg name the ' +
                'var(--l5-color-N) slots they use. May also define .ds-hero, .ds-headline, ' +
                '.ds-label and .ds-plot utility classes that apply them. ' +
                `Two floors are rejected rather than warned about: --ds-type-label is the video's ` +
                `smallest type, read across a room rather than at a desk, so at least ` +
                `${MIN_TYPE_PCT}% of the canvas height; and --ds-margin is at least ` +
                `${MIN_MARGIN_PX}px.`,
        ),
});
export type WriteDesignSystemArgs = z.infer<typeof writeDesignSystemSchema>;

/**
 * Reasons the submitted CSS can't serve as the video's system. Every check names the token at fault:
 * the agent re-submits the whole block, so a finding it can't locate costs a round trip.
 */
function rejections(css: string, canvasHeight: number): string[] {
    const tokens = parseDesignTokens(css);
    const missing = REQUIRED_DESIGN_TOKENS.filter((name) => !tokens.has(name));
    if (missing.length > 0) {
        return [
            `Missing ${missing.length} required token(s): ${missing.join(', ')}. Every graphic in ` +
                `this video is sized and drawn from these, so one left undeclared is one each ` +
                `composition invents for itself.`,
        ];
    }

    const problems: string[] = [];
    const [hero, headline, label] = TYPE_TOKENS.map((name) => tokenPx(tokens, name));
    if (hero === null || headline === null || label === null) {
        problems.push(
            `${TYPE_TOKENS.join(', ')} must each be a px size — they are the video's type scale.`,
        );
    } else {
        if (headline > hero) {
            problems.push(
                `--ds-type-headline (${headline}px) is larger than --ds-type-hero (${hero}px), ` +
                    `which inverts the scale. Order them hero ≥ headline ≥ label.`,
            );
        }
        if (label > headline) {
            problems.push(
                `--ds-type-label (${label}px) is larger than --ds-type-headline (${headline}px), ` +
                    `which inverts the scale. Order them hero ≥ headline ≥ label.`,
            );
        }
        const floorPx = typeFloorPx(canvasHeight);
        if (label < floorPx) {
            problems.push(
                `--ds-type-label (${label}px) is below the ${MIN_TYPE_PCT}% readable norm for ` +
                    `motion-graphic text, which is ${floorPx}px on this ${canvasHeight}px-tall ` +
                    `canvas. Raise it — the smallest size in the scale is still on-screen copy.`,
            );
        }
    }

    const margin = tokenPx(tokens, '--ds-margin');
    if (margin === null || margin < MIN_MARGIN_PX) {
        problems.push(
            `--ds-margin (${tokens.get('--ds-margin')}) must be at least ${MIN_MARGIN_PX}px — ` +
                `content inside that gutter reads as cramped at the canvas edge, and the bounds ` +
                `lint reports it.`,
        );
    }

    const plot = plotBoxOf(tokens);
    if (!plot) {
        problems.push(
            `--ds-plot-x, --ds-plot-y, --ds-plot-w, --ds-plot-h and --ds-baseline-y must each be ` +
                `a px value — they are the one box every data graphic is drawn in.`,
        );
    } else if (Math.abs(plot.baselineY - (plot.y + plot.height)) > BASELINE_TOL_PX) {
        problems.push(
            `--ds-baseline-y (${plot.baselineY}px) is not the foot of the plot box ` +
                `(--ds-plot-y ${plot.y}px + --ds-plot-h ${plot.height}px = ` +
                `${plot.y + plot.height}px). Series are anchored to the baseline and the box is ` +
                `what they are measured in, so the two disagreeing puts every chart off its axis.`,
        );
    }

    return problems;
}

/**
 * Authors the one design system the whole video inherits. Video-level, so unlike the other design
 * tools it takes no scene_id: it is called once, before the first plan, and what it stores is the
 * contract every composition written afterwards is measured against by the design-system lint.
 */
/** WCAG AA for large text, which is what a design system's type sizes always are. */
const LARGE_TEXT_CONTRAST = 3;

/**
 * The colour a token resolves to, as hex. A token names either a literal or one of the video's
 * palette slots (`var(--l5-color-2)`), and only the slots need the template to resolve.
 */
function resolveColour(raw: string | undefined, palette: number[]): string | null {
    if (!raw) return null;
    const slot = raw.match(/--l5-color-(\d+)/);
    if (slot) {
        const colour = palette[Number(slot[1])];
        return colour === undefined ? null : intToHex(colour);
    }
    const literal = raw.match(/#[0-9a-f]{3,8}|rgba?\([^)]+\)/i);
    return literal ? literal[0] : null;
}

/**
 * A note about the accent, when it is too close to the background to be read as words.
 *
 * Not a rejection: an accent is chosen to read as a *shape*, and the palette may hold nothing that
 * clears the text threshold — refusing the system would be a block with no fix, which is the worst
 * thing this tool can do. But an accent at 2.4:1 is where the contrast lint's findings come from,
 * a dozen a build, so the author is better told now than told twelve times later.
 */
function accentReadabilityNote(css: string, palette: number[]): string | null {
    const tokens = parseDesignTokens(css);
    const accent = parseColor(resolveColour(tokens.get('--ds-accent'), palette) ?? '');
    const background = parseColor(resolveColour(tokens.get('--ds-bg'), palette) ?? '');
    if (!accent || !background) return null;
    const ratio = contrastRatio(accent, background);
    if (ratio >= LARGE_TEXT_CONTRAST) return null;
    return (
        `Note: --ds-accent reads at ${ratio.toFixed(2)}:1 against --ds-bg, under the ` +
        `${LARGE_TEXT_CONTRAST}:1 a viewer needs for text. It works for shapes; set type in ` +
        `var(--ds-ink) and keep the accent for what is drawn, or the contrast lint will report ` +
        `every line you colour with it.`
    );
}

export class WriteDesignSystemTool extends Tool<typeof writeDesignSystemSchema> {
    protected override readonly schema = writeDesignSystemSchema;
    protected override readonly toolName = TOOL_NAMES.writeDesignSystem;
    protected override readonly toolDescription =
        "Author the video's design system: the type scale, spacing, stroke weight, chart box and " +
        'colour roles that every graphic in this video is built from. Call this ONCE, before the ' +
        `first ${TOOL_NAMES.writePlan}; it hands back a \`:root\` block to open every composition ` +
        'with. Returns an error listing what is missing or inconsistent if the tokens do not hold ' +
        'together; fix and call again.';

    /**
     * Names the type floor in pixels, for this video's own canvas. The floor is a percentage of
     * canvas height and the field wants px, so an author reading only the percentage has to convert
     * — and a video is authored at any of four reference resolutions, so a figure quoted for one of
     * them would be wrong on the other three. Left as the percentage alone when there is no
     * resolution to read (a tool built without a luminary, as the schema guardrail spec does).
     */
    protected override extendParameters(
        parameters: ToolDefinition['function']['parameters'],
    ): ToolDefinition['function']['parameters'] {
        const canvasHeight = this.luminary?.referenceResolution?.height;
        if (!canvasHeight) {
            return parameters;
        }
        const css = parameters.properties?.css as { description?: string } | undefined;
        if (!css?.description) {
            return parameters;
        }
        return {
            ...parameters,
            properties: {
                ...parameters.properties,
                css: {
                    ...css,
                    description: css.description.replace(
                        `${MIN_TYPE_PCT}% of the canvas height`,
                        `${MIN_TYPE_PCT}% of this video's ${canvasHeight}px canvas height, which is ` +
                            `${typeFloorPx(canvasHeight)}px`,
                    ),
                },
            },
        };
    }

    public getToolDisplayName(): string {
        return 'Designing the system';
    }

    public get statusIcon(): string {
        return 'palette';
    }

    public async execute(
        args: WriteDesignSystemArgs,
        _context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const problems = rejections(args.css, this.luminary.referenceResolution.height);
        if (problems.length > 0) {
            return {
                result: `The design system was not accepted:\n${problems
                    .map((problem) => `- ${problem}`)
                    .join('\n')}`,
                isSuccess: false,
            };
        }

        this.dispatch(setHtmlDesignSystem({ look: args.look, css: args.css }));
        const palette = (this.luminary.template?.colors ?? []).map((slot) => slot.color);
        const note = accentReadabilityNote(args.css, palette);
        return {
            result:
                'Design system stored. Open every composition you write for this video with it, ' +
                `before your own styles:\n\n<style>\n${args.css}\n</style>` +
                (note ? `\n\n${note}` : ''),
            isSuccess: true,
        };
    }
}
